import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Candidate|Eligibilities|';
const sharedProperties: Pick<LocDef, 'feature' | 'portal'> = {
	feature: HubFeature.ExamAuthorization,
	portal: 'SP',
};

type LocNameStart = 'SPEligibilitiesSection';
export type SPEligibilitiesSectionLocNames =
	| `${LocNameStart}Description`
	| `${LocNameStart}ButtonGrantTrigger`
	| `${LocNameStart}ButtonHideToggle`
	| `${LocNameStart}ButtonShowToggle`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}LabelCode`
	| `${LocNameStart}LabelGroup`
	| `${LocNameStart}LabelManual`
	| `${LocNameStart}LabelClaimed`
	| `${LocNameStart}LabelPushed`
	| `${LocNameStart}LabelAuthorizationId`
	| `${LocNameStart}LabelAuthStatePackage`
	| `${LocNameStart}LabelVendorState`
	| `${LocNameStart}LabelActiveDate`
	| `${LocNameStart}LabelPushedDate`
	| `${LocNameStart}LabelExpireDate`
	| `${LocNameStart}LabelSendOkDate`
	| `${LocNameStart}LabelNameFilter`
	| `${LocNameStart}LabelGroupFilter`
	| `${LocNameStart}LabelPushedFilter`
	| `${LocNameStart}TitleActive`
	| `${LocNameStart}TitleFuture`
	| `${LocNameStart}TitleExpired`
	| `${LocNameStart}ButtonEdit`
	| `${LocNameStart}ButtonRevoke`
	| `${LocNameStart}ButtonClaim`
	| `${LocNameStart}LabelRevoked`
	| `${LocNameStart}ButtonUnrevoke`;

export const spEligibilitiesSectionLocs: LocDefs<SPEligibilitiesSectionLocNames> = {
	SPEligibilitiesSectionDescription: {
		defaultValue: '',
		description: 'Section description for eligibilities page',
		displayPath: `${dpRoot}|Section Description|`,
		displayType: 'Content',
		htmlAllowed: true,
		...sharedProperties,
	},
	SPEligibilitiesSectionButtonGrantTrigger: {
		defaultValue: 'Grant Eligibility',
		description: 'Button to grant eligibility',
		displayPath: `${dpRoot}Grant Trigger`,
		displayType: 'Button',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.Button.GrantTrigger',
		...sharedProperties,
	},
	SPEligibilitiesSectionButtonHideToggle: {
		defaultValue: 'Hide Eligibilities',
		description: 'Button to hide eligibilities',
		displayPath: `${dpRoot}Hide Toggle`,
		displayType: 'Button',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.Button.HideToggle',
		...sharedProperties,
	},
	SPEligibilitiesSectionButtonShowToggle: {
		defaultValue: 'Show Eligibilities',
		description: 'Button to show eligibilities',
		displayPath: `${dpRoot}Show Toggle`,
		displayType: 'Button',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.Button.ShowToggle',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelName: {
		defaultValue: 'Name',
		description: 'Label for Name',
		displayPath: `${dpRoot}Name`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.Name',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelCode: {
		defaultValue: 'Code',
		description: 'Label for Code',
		displayPath: `${dpRoot}Code`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.Code',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelGroup: {
		defaultValue: 'Group',
		description: 'Label for Group',
		displayPath: `${dpRoot}Group`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.Group',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelManual: {
		defaultValue: 'Manual',
		description: 'Label for Manual',
		displayPath: `${dpRoot}Manual`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.Manual',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelClaimed: {
		defaultValue: 'Claimed',
		description: 'Label for Claimed',
		displayPath: `${dpRoot}Claimed`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.Claimed',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelPushed: {
		defaultValue: 'Pushed',
		description: 'Label for Pushed',
		displayPath: `${dpRoot}Pushed`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.Pushed',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelAuthorizationId: {
		defaultValue: 'Authorization ID',
		description: 'Label for Authorization ID',
		displayPath: `${dpRoot}Authorization ID`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.AuthorizationId',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelAuthStatePackage: {
		defaultValue: 'Auth State Package',
		description: 'Label for Auth State Package',
		displayPath: `${dpRoot}Auth State Package`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.AuthStatePackage',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelVendorState: {
		defaultValue: 'Vendor Request State',
		description: 'Label for Vendor Request State',
		displayPath: `${dpRoot}Vendor Request State`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.VendorState',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelActiveDate: {
		defaultValue: 'Eligible Date',
		description: 'Label for Eligible Date',
		displayPath: `${dpRoot}Eligible Date`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.ActiveDate',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelPushedDate: {
		defaultValue: 'Pushed Date',
		description: 'Label for Pushed Date',
		displayPath: `${dpRoot}Pushed Date`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.PushedDate',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelExpireDate: {
		defaultValue: 'Expiration Date',
		description: 'Label for Expiration Date',
		displayPath: `${dpRoot}Expiration Date`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.ExpireDate',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelSendOkDate: {
		defaultValue: 'Send OK Date',
		description: 'Label for Send OK Date',
		displayPath: `${dpRoot}Send OK Date`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.ColumnHeading.SendOkDate',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelNameFilter: {
		defaultValue: 'Filter by Exam',
		description: 'Label for Name filter',
		displayPath: `${dpRoot}Name Filter`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.FieldLabel.NameFilter',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelGroupFilter: {
		defaultValue: 'Filter by Group',
		description: 'Label for Group filter',
		displayPath: `${dpRoot}Group Filter`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.FieldLabel.GroupFilter',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelPushedFilter: {
		defaultValue: 'Filter by Pushed State',
		description: 'Label for Pushed State filter',
		displayPath: `${dpRoot}Pushed Filter`,
		displayType: 'Label',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.FieldLabel.PushedFilter',
		...sharedProperties,
	},
	SPEligibilitiesSectionTitleActive: {
		defaultValue: 'Active Eligibilities Table',
		description: 'Title for Active Eligibilities',
		displayPath: `${dpRoot}Title Active`,
		displayType: 'Title',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.Title.ActiveTable',
		...sharedProperties,
	},
	SPEligibilitiesSectionTitleFuture: {
		defaultValue: 'Future Eligibilities Table',
		description: 'Title for Future Eligibilities',
		displayPath: `${dpRoot}Title Future`,
		displayType: 'Title',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.Title.FutureTable',
		...sharedProperties,
	},
	SPEligibilitiesSectionTitleExpired: {
		defaultValue: 'Expired Eligibilities Table',
		description: 'Title for Expired Eligibilities',
		displayPath: `${dpRoot}Title Expired`,
		displayType: 'Title',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.Title.ExpiredTable',
		...sharedProperties,
	},
	SPEligibilitiesSectionButtonEdit: {
		defaultValue: 'Edit',
		description: 'Button to edit the eligibility.',
		displayPath: `${dpRoot}Edit`,
		displayType: 'Button',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.Button.Edit',
		...sharedProperties,
	},
	SPEligibilitiesSectionButtonRevoke: {
		defaultValue: 'Revoke',
		description: 'Button to revoke the eligibility.',
		displayPath: `${dpRoot}Revoke`,
		displayType: 'Button',
		htmlAllowed: false,
		shared: true,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.Button.Revoke',
		...sharedProperties,
	},
	SPEligibilitiesSectionButtonClaim: {
		defaultValue: 'Claim',
		description: 'Button to claim the eligibility.',
		displayPath: `${dpRoot}Claim`,
		displayType: 'Button',
		htmlAllowed: false,
		legacyName: 'Exam.Authorization/Candidates.Eligibilities.Button.Claim',
		...sharedProperties,
	},
	SPEligibilitiesSectionLabelRevoked: {
		defaultValue: 'Revoked',
		description: 'Label for Revoked',
		displayPath: `${dpRoot}Revoked`,
		displayType: 'Label',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPEligibilitiesSectionButtonUnrevoke: {
		defaultValue: 'Unrevoke',
		description: 'Button to unrevoke the eligibility.',
		displayPath: `${dpRoot}Unrevoke`,
		displayType: 'Button',
		htmlAllowed: false,
		...sharedProperties,
	},
};

export type SPEligibilitiesSectionLocType = LocMap<SPEligibilitiesSectionLocNames>;

export const LOC_KEYS = MapLocKeys<SPEligibilitiesSectionLocNames>(spEligibilitiesSectionLocs);
